import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { extend, injectStore, NgtArgs } from 'angular-three';
import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { DicePhysicsService } from '../../services/dice-physics.service';
import {
  createDiceMesh,
  DiceGeometryParams,
} from '../../utils/dice-geometry.util';
import {
  DiceRollerConfig,
  DiceRollResult,
  ThrowParameters,
  PhysicsFrame,
  DieMaterial,
  SurfaceMaterial,
} from '../../models/dice-roller.types';
import { DEFAULT_DICE_CONFIG } from '../../models/dice-faces.const';

// Extend Three.js classes for Angular Three
extend({
  Mesh: THREE.Mesh,
  Group: THREE.Group,
  BoxGeometry: THREE.BoxGeometry,
  PlaneGeometry: THREE.PlaneGeometry,
  MeshStandardMaterial: THREE.MeshStandardMaterial,
  MeshBasicMaterial: THREE.MeshBasicMaterial,
  AmbientLight: THREE.AmbientLight,
  DirectionalLight: THREE.DirectionalLight,
  ShadowMaterial: THREE.ShadowMaterial,
});

/**
 * Dice Roller Component
 *
 * Angular Three component for 3D dice with record-and-replay physics.
 * Achieves 100% visual accuracy by replaying exact simulation frames.
 *
 * **Features:**
 * - 100% accuracy: Simulation records frames, component replays them
 * - Predetermined mode: Guaranteed to land on target value
 * - Random mode: Generates random outcomes
 * - Scene rotation awareness: Adapts for flat/vertical AR scenes
 *
 * **Usage:**
 * ```html
 * <lib-dice-roller 
 *   [config]="diceConfig" 
 *   (rollComplete)="onRollComplete($event)" 
 * />
 * ```
 *
 * **API (via scene.userData):**
 * ```typescript
 * const diceRoller = scene.userData['diceRoller'];
 * await diceRoller.roll(targetValue); // Predetermined
 * await diceRoller.rollRandom();      // Random
 * ```
 */
@Component({
  selector: 'lib-dice-roller',
  standalone: true,
  imports: [NgtArgs],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!-- Dice Mesh (playback-driven, position updated from recorded frames) -->
    <ngt-primitive
      *args="[diceGeometry()]"
      [parameters]="{
        position: dicePosition(),
        rotation: diceRotation()
      }"
    />
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `],
})
export class UiDiceRollerComponent {
  private store = injectStore();
  private physics = inject(DicePhysicsService);

  // Inputs
  config = input<DiceRollerConfig>(DEFAULT_DICE_CONFIG);
  enableCameraControls = input(false);

  // Outputs
  rollStart = output<void>();
  rollComplete = output<DiceRollResult>();

  // State
  private diceBody = signal<CANNON.Body | null>(null);
  private diceMesh = signal<THREE.Group | null>(null);
  private isRolling = signal(false);
  
  // Position and rotation for template binding
  // CRITICAL: Die must start above floor top surface (Z=0.2)
  protected dicePosition = signal<[number, number, number]>([0, 0, 1.5]);
  protected diceRotation = signal<[number, number, number]>([0, 0, 0]);

  // Computed geometry
  protected diceGeometry = computed(() => {
    const cfg = this.config();
    const params: DiceGeometryParams = {
      size: cfg.size || 0.6,
      segments: cfg.segments || 40,
      edgeRadius: cfg.edgeRadius || 0.08,
      notchRadius: cfg.notchRadius || 0.15,
      notchDepth: cfg.notchDepth || 0.17,
    };
    const diceMesh = createDiceMesh(params);
    
    // Apply scale if provided (maintains proportions)
    const scale = cfg.scale || 1.0;
    diceMesh.scale.set(scale, scale, scale);
    
    return diceMesh;
  });

  // Scene rotation for group
  protected sceneGroupRotation = computed(() => {
    const rotation = this.config().sceneRotation || [0, 0, 0];
    return rotation as [number, number, number];
  });

  // Floor shadow material
  protected floorMaterial = computed(
    () =>
      new THREE.ShadowMaterial({
        opacity: 0.3,
      })
  );

  protected readonly Math = Math;

  constructor() {
    // Store reference to dice mesh when geometry changes
    effect(() => {
      const mesh = this.diceGeometry();
      if (mesh) {
        this.diceMesh.set(mesh);
        mesh.position.set(0, 2, 0);
      }
    });

    // Expose API via scene.userData
    effect(() => {
      const scene = this.store.get().scene;
      if (scene) {
        scene.userData['diceRoller'] = {
          roll: this.roll.bind(this),
          isRolling: () => this.isRolling(),
          getState: () => this.physics.rollState(),
        };
      }
    });

    // Create dice body for positioning (playback updates this directly)
    // Note: Physics simulation happens in DicePhysicsService.simulationWorld
    effect(() => {
      const cfg = this.config();

      // Use configured start position or default
      const startPos = cfg.startPosition || [0, 2, 0];
      
      // Create dice body for position tracking during playback
      const dieSize = cfg.size || 0.6;
      const body = new CANNON.Body({
        mass: 1,
        shape: new CANNON.Box(
          new CANNON.Vec3(dieSize / 2, dieSize / 2, dieSize / 2)
        ),
        position: new CANNON.Vec3(...startPos),
        allowSleep: false
      });
      
      this.diceBody.set(body);
      
      // Update initial visual position
      this.dicePosition.set(startPos);

      // Cleanup on destroy
      return () => {
        this.diceBody.set(null);
      };
    });

    // Sync dice mesh position/rotation with physics body
    // NOTE: Animation now handled by playbackRecordedFrames() for 100% accuracy
    // This effect is kept for any non-roll physics updates
    effect(() => {
      const body = this.diceBody();
      const rolling = this.isRolling();

      if (!body || rolling) {
        return; // Skip if no body or if rolling (playback handles animation)
      }

      // Only update position when NOT rolling
      this.dicePosition.set([body.position.x, body.position.y, body.position.z]);
      const euler = new THREE.Euler().setFromQuaternion(
        new THREE.Quaternion(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w)
      );
      this.diceRotation.set([euler.x, euler.y, euler.z]);
    });
  }

  /**
   * Resets dice state. Call when restarting game.
   */
  reset(): void {
    // Reset physics service
    this.physics.reset();
    
    // Reset dice body to initial state
    const body = this.diceBody();
    if (body) {
      const startPos = this.config().startPosition || [0, 2, 0];
      body.position.set(...startPos);
      body.quaternion.set(0, 0, 0, 1); // Identity quaternion
      body.velocity.setZero();
      body.angularVelocity.setZero();
      body.force.setZero();
      body.torque.setZero();
      
      // Update visual position
      this.dicePosition.set(startPos);
      this.diceRotation.set([0, 0, 0]);
    }
    
    // Reset rolling state
    this.isRolling.set(false);
  }

  /**
   * Plays back recorded physics frames for 100% accurate animation.
   * Also triggers collision audio based on recorded collision events.
   * 
   * This guarantees visual accuracy by replaying exact simulation frames
   * instead of re-simulating physics (which causes divergence).
   * 
   * @param frames Array of recorded physics frames from simulation
   * @returns Promise resolving when playback completes
   */
  private playbackRecordedFrames(frames: PhysicsFrame[]): Promise<void> {
    return new Promise((resolve) => {
      let frameIndex = 0;
      const body = this.diceBody();
      const config = this.config();
      
      if (!body || frames.length === 0) {
        resolve();
        return;
      }
      
      const playFrame = () => {
        if (frameIndex >= frames.length) {
          // Playback complete
          this.isRolling.set(false);
          this.physics['rollState'].set('idle');
          resolve();
          return;
        }
        
        // Apply current frame to body and signals
        const frame = frames[frameIndex];
        body.position.set(...frame.position);
        body.quaternion.set(...frame.quaternion);
        
        // Update signals for Three.js mesh
        this.dicePosition.set(frame.position);
        const euler = new THREE.Euler().setFromQuaternion(
          new THREE.Quaternion(...frame.quaternion)
        );
        this.diceRotation.set([euler.x, euler.y, euler.z]);
        
        // Play collision audio if present in this frame
        if (frame.collision && !config.mute) {
          this.playCollisionSound(
            frame.collision.type,
            frame.collision.intensity,
            config.dieMaterial || 'plastic',
            config.surfaceMaterial || 'felt'
          );
        }
        
        frameIndex++;
        requestAnimationFrame(playFrame);
      };
      
      playFrame();
    });
  }
  
  /**
   * Plays collision sound based on type and materials.
   * 
   * Sound files are selected using the following convention:
   * - Die-to-die: `/assets/sfx/dicehit/dicehit_[material][1-3].mp3`
   * - Die-to-surface: `/assets/sfx/surface/surface_[material][1-3].mp3`
   * 
   * Volume is scaled by collision intensity (0.2-0.8 range) for realistic audio.
   * Random variation (1-3) is selected to prevent repetitive sounds.
   * 
   * @param type Collision type (die-to-die or die-to-surface)
   * @param intensity Impact intensity normalized to 0-1
   * @param dieMaterial Die material for sound selection
   * @param surfaceMaterial Surface material for sound selection
   */
  private playCollisionSound(
    type: 'die-to-die' | 'die-to-surface',
    intensity: number,
    dieMaterial: DieMaterial,
    surfaceMaterial: SurfaceMaterial
  ): void {
    // Determine sound file path based on collision type
    const soundFolder = type === 'die-to-die' ? 'dicehit' : 'surface';
    const material = type === 'die-to-die' ? dieMaterial : surfaceMaterial;
    
    // Each material has 3 variations (1-3)
    const variation = Math.floor(Math.random() * 3) + 1;
    const soundPath = `/assets/sfx/${soundFolder}/${soundFolder}_${material}${variation}.mp3`;
    
    // Create and play audio
    const audio = new Audio(soundPath);
    audio.volume = Math.max(0.2, Math.min(0.8, intensity)); // Volume based on intensity (0.2-0.8 range)
    
    // Play sound (errors are expected if files are missing or browser blocks autoplay)
    audio.play().catch(() => {
      // Silently ignore audio errors to prevent breaking animation
      // Common causes: missing audio files, browser autoplay restrictions
    });
  }

  /**
   * Rolls the die to a specific target value.
   * 
   * @param targetValue Target face value (1-6)
   * @returns Promise resolving to roll result
   */
  async roll(targetValue: number): Promise<DiceRollResult> {
    if (this.isRolling()) {
      throw new Error('Roll already in progress');
    }

    const body = this.diceBody();
    if (!body) {
      throw new Error('Dice body not initialized');
    }

    this.isRolling.set(true);
    this.rollStart.emit();

    try {
      // Pre-calculate valid throw in simulation world
      const params = await this.physics.findValidThrow(
        targetValue,
        this.config()
      );

      // Set initial position and rotation for playback
      body.position.set(...params.startPos);
      body.quaternion.set(
        params.startRotation.x,
        params.startRotation.y,
        params.startRotation.z,
        params.startRotation.w
      );
      
      // Playback recorded frames for 100% accurate animation
      await this.playbackRecordedFrames(params.recordedFrames);

      const result: DiceRollResult = {
        values: [targetValue], // Use simulation result for deterministic gameplay
        duration: 0,
        attemptCount: 1,
        success: true, // Simulation already validated this matches target
      };

      this.rollComplete.emit(result);
      return result;
    } finally {
      this.isRolling.set(false);
    }
  }

  /**
   * Rolls dice randomly (no pre-calculation).
  /**
   * Called when dice mesh is created in template.
   * Stores reference for position/rotation syncing.
   */
  onDiceMeshCreated(mesh: THREE.Group) {
    this.diceMesh.set(mesh);
  }
}
